import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { View, Text, Button } from '@olxbr/design-system'

import Title from '../../atoms/Title'
import Lurker from '../../../util/lurker'
import Tracker from '../../../util/tracker'
import { registerAndValidateAccountWithToken } from '../../../api/registerApiCall'
import LogoGreyMessage from '../../atoms/LogoGreyMessage'
import getSessionTokenInfos from '../../../util/sessionTokenGetter'

const lurker = new Lurker('signup')
const tracker = new Tracker('signup')

const Content = styled(View)`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
`

const initialErrors = {
  showGenericError: false,
  showInvalidTokenError: false,
  showRateLimitError: false,
  showConflictError: false,
}

const LogoWrapper = styled(View)`
  margin-bottom: 32px;
`

const Description = styled(Text)`
  margin-top: 24px;
  width: 328px;
  height: 72px;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
`

const TryAgain = styled(Text)`
  margin-top: 16px;
  width: 328px;
  height: 24px;
  font-size: 16px;
  line-height: 24px;
  text-align: center;
`

const ButtonWrapper = styled(View)`
  margin-top: 48px;
  width: 100%;
  height: 50px;
`

class RegisterWithTokenTemplate extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      errors: initialErrors,
    }
  }

  componentDidMount() {
    this.doRegistration()
  }

  doRegistration = async () => {
    const { token, onRegisterSuccess, onError } = this.props
    const sessionIds = await getSessionTokenInfos()
    try {
      const registerCall = registerAndValidateAccountWithToken
      const { data } = await registerCall(token, sessionIds)
      if (window) {
        const { dataLayer } = window
        if (dataLayer) {
          dataLayer.push({ event: 'email_signup_success', user_id: data?.publicAccountId })
        }
      }
      lurker.viewMessageEvt('email_signup_success', {
        remote_addr: data.remoteAddress,
      })
      tracker.viewMessageEvt('sign_up_email', {
        remote_addr: data.remoteAddress,
      })
      if (onRegisterSuccess) {
        onRegisterSuccess(data.token)
      }
      return null
    } catch (error) {
      const {
        response: { status, data: { message } = {} } = {},
        message: exceptionMessage = 'unknown',
      } = error
      if (onError) {
        onError(error)
      }

      if (status === 429) return this.handle429()
      if (status === 409) return this.handle409(message)
      if (status) return this.handleError(status, message)
      return this.handleUnexpectedError(exceptionMessage)
    }
  }

  handle429 = () => {
    this.handleExpectedStatusCode(429, 'rate_limit_exceeded', 'showRateLimitError')
  }

  handle409 = message => {
    this.handleExpectedStatusCode(
      409,
      typeof message === 'string' ? message.toLowerCase() : 'conflict_error',
      'showConflictError'
    )
  }

  handleError = (status, message = 'unexpected_status_code') => {
    switch (message) {
      case 'INVALID_TOKEN':
        this.handleExpectedStatusCode(status, message.toLowerCase(), 'showInvalidTokenError')
        break
      default:
        this.handleUnexpectedStatusCode(status, message)
    }
  }

  handleExpectedStatusCode = (status, message, error) => {
    lurker.viewMessageEvt('error', {
      accounts_reason: message,
      accounts_status_code: status,
    })
    this.showError(error)
  }

  handleUnexpectedStatusCode = (status, message) => {
    lurker.viewMessageEvt('error', {
      accounts_reason: 'unexpected_status_code',
      accounts_status_code: status,
      accounts_error_message:
        message && typeof message === 'string' ? `${message.toLowerCase()}` : 'unknown',
    })
    this.showError('showGenericError')
  }

  handleUnexpectedError = (message = 'unknown') => {
    lurker.viewMessageEvt('error', {
      accounts_reason: 'unexpected_error',
      accounts_error_message: message,
    })
    this.showError('showGenericError')
  }

  showError = error => {
    this.setState({
      errors: { ...initialErrors, [error]: true },
    })
  }

  render() {
    const { onRetryRegister } = this.props
    const { errors } = this.state
    if (Object.values(errors).some(i => i)) {
      return (
        <Content>
          <LogoWrapper>
            <LogoGreyMessage />
          </LogoWrapper>
          <Title>Não foi possível criar a sua conta</Title>
          {errors.showConflictError ? (
            <>
              <Description>
                O documento informado no tipo de conta já está sendo utilizado. Por favor, verifique
                o documento digitado ou tente recuperar uma conta antiga.
              </Description>
            </>
          ) : (
            <>
              <Description>
                O botão de verificação enviado por e-mail possui validade, por isso não conseguimos
                concluir a criação de sua conta.
              </Description>
              <TryAgain>Por favor, tente novamente.</TryAgain>
            </>
          )}

          <ButtonWrapper>
            <Button onClick={onRetryRegister} size="large" fluid>
              Tentar novamente
            </Button>
          </ButtonWrapper>
        </Content>
      )
    }
    return null
  }
}

RegisterWithTokenTemplate.propTypes = {
  abTests: PropTypes.string,
  onError: PropTypes.func.isRequired,
  onRegisterSuccess: PropTypes.func.isRequired,
  onRetryRegister: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
}

export default RegisterWithTokenTemplate
